import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";

import { LoadingOutlined } from "@ant-design/icons";
import { Skeleton, Spin } from "antd";
import debounce from "lodash/debounce";
import isEqual from "lodash/isEqual";
import moment from "moment";
import { toast } from "react-toastify";

import DatePicker from "../../Common/DatePicker/DatePicker";
import StyledScrollbar from "../../Common/UIComponents/StyledScrollbar";
import EnvAnalyticsHeader from "./Components/Header/EnvAnalyticsHeader";
import AnalyticsTable from "./Components/Table/EnvAnalyticsTable";

import ResultSummary from "./Components/ResultSummary/ResultSummary";
import ResultTestAnalyticsChart from "./Components/ResultTestAnalytics/ResultTestAnalyticsChart";
import TestVolumeTrendChart from "./Components/VolumeTrend/TestVolumeTrendChart";
import SwabList from "./Components/SwabList/SwabList";

import { envAnalyticsDataFetch, envTestResultAnalyticsFetch } from "../../../actions/envAnalytics";
import { normalizeAnalyticsData, normalizeTestResultAnalytics } from "./utils";

import { ReactComponent as CalendarIcon } from "../../../assets/images/environment/CalendarEnv.svg";

import "./EnvAnalyticsTab.css";

const headers = ["Swab ID", "Last submitted on", "No of days since last submission"];
const headers_2 = ["Swab ID", "Status", "Out of Spec Samples"];
const DEFAULT_DATE_RANGE_DAYS = 30;

const formatDisplayDateRange = (startDate, endDate) => (
  `${moment(startDate).format("MMM DD, YYYY")} - ${moment(endDate).format("MMM DD, YYYY")}`
);

const getDefaultDateRange = () => {
  const endDate = moment();
  const startDate = moment().subtract(DEFAULT_DATE_RANGE_DAYS, "days");

  return {
    startDate: startDate.format("YYYY-MM-DD"),
    endDate: endDate.format("YYYY-MM-DD"),
    display: formatDisplayDateRange(startDate, endDate),
  };
};

const parseDisplayDateRange = (dateRange) => {
  const [startDate, endDate] = dateRange.split(" - ");

  return {
    startDate: moment(startDate, "MMM D, YYYY").format("YYYY-MM-DD"),
    endDate: moment(endDate, "MMM D, YYYY").format("YYYY-MM-DD"),
  };
};

const SAMPLE_SUMMARY_CARDS = [
  {
    label: "Samples Received",
    valueKey: "samples_received",
    swabListCategory: "samples_received",
    textColor: "var(--color-analytics-pink)",
    backgroundColor: "var(--color-analytics-pink-bg)",
  },
  {
    label: "Samples in Testing",
    valueKey: "samples_in_testing",
    swabListCategory: "samples_in_testing",
    textColor: "var(--color-analytics-rust)",
    backgroundColor: "var(--color-analytics-rust-bg)",
  },
  {
    label: "Test Result Available",
    valueKey: "results_available",
    swabListCategory: "results_available",
    textColor: "var(--color-analytics-purple)",
    backgroundColor: "var(--color-analytics-purple-bg)",
  },
];

export default function EnvAnalyticsTab() {
  const [summaryDateRange, setSummaryDateRange] = useState("");
  const [summaryPrevDateRange, setSummaryPrevDateRange] = useState("");
  const [resultTestDateRange, setResultTestDateRange] = useState("");
  const [resultTestPrevDateRange, setResultTestPrevDateRange] = useState("");
  const [analyticsData, setAnalyticsData] = useState({
    spec_details: null,
    test_details: null,
    submission_details: null,
    ratio_details: null,
    sample_summary: null,
    map_test_details: null,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isResultTestLoading, setIsResultTestLoading] = useState(true);
  const [isInitial, setIsInitial] = useState(true);
  const [swabListModal, setSwabListModal] = useState({
    open: false,
    category: null,
  });
  const abortControllerRef = useRef(null);
  const resultTestAbortControllerRef = useRef(null);
  const [selectedMap, setSelectedMap] = useState(null);
  const [selectedSecondaryField, setSelectedSecondaryField] = useState(null);

  const fetchSummaryAnalytics = useCallback(async ({ startDate, endDate, signal }) => {
    try {
      const response = await envAnalyticsDataFetch({
        date_from: startDate,
        date_to: endDate,
      }, signal);

      if (response.canceled) {
        return null;
      }

      if (response.success) {
        return normalizeAnalyticsData(response.result);
      }

      toast.error("Failed to load analytics specs");
      return null;
    } catch (error) {
      if (error.name !== "AbortError") {
        toast.error("Error fetching analytics data");
      }
      return null;
    }
  }, []);

  const fetchTestResultAnalytics = useCallback(async ({
    startDate,
    endDate,
    mapTitle,
    secondaryField,
    signal,
  }) => {
    try {
      const payload = {
        date_from: startDate,
        date_to: endDate,
      };

      if (mapTitle) {
        payload.map = mapTitle;
      }

      if (secondaryField) {
        payload.sample_id_field = secondaryField;
      }

      const response = await envTestResultAnalyticsFetch(payload, signal);

      if (response.canceled) {
        return null;
      }

      if (response.success) {
        return normalizeTestResultAnalytics(response.result, secondaryField);
      }

      toast.error("Failed to load result and test analytics");
      return null;
    } catch (error) {
      if (error.name !== "AbortError") {
        toast.error("Error fetching result and test analytics");
      }
      return null;
    }
  }, []);

  const handleSummaryDateSelection = (date) => {
    if (date === "") {
      return;
    }
    setSummaryDateRange(date);
  };

  const handleResultTestDateSelection = (date) => {
    if (date === "") {
      return;
    }
    setResultTestDateRange(date);
  };

  const getSummaryReportDates = useCallback(async () => {
    try {
      const { startDate, endDate } = parseDisplayDateRange(summaryDateRange);
      const response = await envAnalyticsDataFetch({
        date_from: startDate,
        date_to: endDate,
      });

      if (response.success) {
        return response.dates;
      }

      toast.error("Failed to load analytics specs");
      return null;
    } catch (error) {
      if (error.name !== "AbortError") {
        toast.error("Error fetching analytics data");
      }
      return null;
    }
  }, [summaryDateRange]);

  const getResultTestReportDates = useCallback(async () => {
    try {
      const { startDate, endDate } = parseDisplayDateRange(resultTestDateRange);
      const response = await envAnalyticsDataFetch({
        date_from: startDate,
        date_to: endDate,
      });

      if (response.success) {
        return response.dates;
      }

      toast.error("Failed to load analytics specs");
      return null;
    } catch (error) {
      if (error.name !== "AbortError") {
        toast.error("Error fetching analytics data");
      }
      return null;
    }
  }, [resultTestDateRange]);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setIsResultTestLoading(true);

      try {
        const defaultRange = getDefaultDateRange();

        setSummaryDateRange(defaultRange.display);
        setSummaryPrevDateRange(defaultRange.display);
        setResultTestDateRange(defaultRange.display);
        setResultTestPrevDateRange(defaultRange.display);

        const [summaryData, mapTestDetails] = await Promise.all([
          fetchSummaryAnalytics({
            startDate: defaultRange.startDate,
            endDate: defaultRange.endDate,
          }),
          fetchTestResultAnalytics({
            startDate: defaultRange.startDate,
            endDate: defaultRange.endDate,
          }),
        ]);

        if (summaryData && mapTestDetails) {
          setAnalyticsData({
            ...summaryData,
            map_test_details: mapTestDetails,
          });
        }
      } catch (error) {
        toast.error(error.message);
      } finally {
        setIsLoading(false);
        setIsResultTestLoading(false);
        setIsInitial(false);
      }
    };

    fetchData();
  }, []); // eslint-disable-line

  const handleSummaryDateChange = useCallback(
    debounce(async (startDate, endDate) => {
      setIsLoading(true);
      const formattedStartDate = moment(startDate, "MMM D, YYYY").format("YYYY-MM-DD");
      const formattedEndDate = moment(endDate, "MMM D, YYYY").format("YYYY-MM-DD");

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      const controller = new AbortController();
      abortControllerRef.current = controller;

      try {
        const summaryData = await fetchSummaryAnalytics({
          startDate: formattedStartDate,
          endDate: formattedEndDate,
          signal: controller.signal,
        });

        if (summaryData) {
          setAnalyticsData((prev) => {
            const next = {
              ...prev,
              ...summaryData,
            };

            return isEqual(next, prev) ? prev : next;
          });
        }
      } catch (error) {
        toast.error(error.message);
      } finally {
        if (abortControllerRef.current === controller) {
          setIsLoading(false);
        }
      }
    }, 300),
    [fetchSummaryAnalytics],
  );

  const handleResultTestDateChange = useCallback(
    debounce(async (startDate, endDate) => {
      const formattedStartDate = moment(startDate, "MMM D, YYYY").format("YYYY-MM-DD");
      const formattedEndDate = moment(endDate, "MMM D, YYYY").format("YYYY-MM-DD");

      if (resultTestAbortControllerRef.current) {
        resultTestAbortControllerRef.current.abort();
      }
      const controller = new AbortController();
      resultTestAbortControllerRef.current = controller;

      setIsResultTestLoading(true);

      try {
        const mapTestDetails = await fetchTestResultAnalytics({
          startDate: formattedStartDate,
          endDate: formattedEndDate,
          mapTitle: selectedMap,
          secondaryField: selectedSecondaryField,
          signal: controller.signal,
        });

        if (mapTestDetails) {
          setAnalyticsData((prev) => ({
            ...prev,
            map_test_details: mapTestDetails,
          }));
        }
      } catch (error) {
        toast.error(error.message);
      } finally {
        if (resultTestAbortControllerRef.current === controller) {
          setIsResultTestLoading(false);
        }
      }
    }, 300),
    [fetchTestResultAnalytics, selectedMap, selectedSecondaryField],
  );

  const resetSummaryToDefaultDateRange = useCallback(() => {
    const defaultRange = getDefaultDateRange();
    handleSummaryDateChange.cancel();
    setSummaryDateRange(defaultRange.display);
    setSummaryPrevDateRange(defaultRange.display);
    handleSummaryDateChange(
      moment(defaultRange.startDate).format("MMM D, YYYY"),
      moment(defaultRange.endDate).format("MMM D, YYYY"),
    );
    handleSummaryDateChange.flush();
  }, [handleSummaryDateChange]);

  const resetResultTestToDefaultDateRange = useCallback(() => {
    const defaultRange = getDefaultDateRange();
    handleResultTestDateChange.cancel();
    setResultTestDateRange(defaultRange.display);
    setResultTestPrevDateRange(defaultRange.display);
    handleResultTestDateChange(
      moment(defaultRange.startDate).format("MMM D, YYYY"),
      moment(defaultRange.endDate).format("MMM D, YYYY"),
    );
    handleResultTestDateChange.flush();
  }, [handleResultTestDateChange]);

  const handleResultTestReset = useCallback(async () => {
    const defaultRange = getDefaultDateRange();

    handleResultTestDateChange.cancel();
    setSelectedMap(null);
    setSelectedSecondaryField(null);
    setResultTestDateRange(defaultRange.display);
    setResultTestPrevDateRange(defaultRange.display);

    if (resultTestAbortControllerRef.current) {
      resultTestAbortControllerRef.current.abort();
    }
    const controller = new AbortController();
    resultTestAbortControllerRef.current = controller;

    setIsResultTestLoading(true);

    try {
      const mapTestDetails = await fetchTestResultAnalytics({
        startDate: defaultRange.startDate,
        endDate: defaultRange.endDate,
        mapTitle: null,
        secondaryField: null,
        signal: controller.signal,
      });

      if (mapTestDetails) {
        setAnalyticsData((prev) => ({
          ...prev,
          map_test_details: mapTestDetails,
        }));
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      if (resultTestAbortControllerRef.current === controller) {
        setIsResultTestLoading(false);
      }
    }
  }, [fetchTestResultAnalytics, handleResultTestDateChange]);

  const handleMapChange = useCallback(async (mapTitle) => {
    setSelectedMap(mapTitle);

    const { startDate, endDate } = parseDisplayDateRange(resultTestDateRange);

    if (resultTestAbortControllerRef.current) {
      resultTestAbortControllerRef.current.abort();
    }
    const controller = new AbortController();
    resultTestAbortControllerRef.current = controller;

    setIsResultTestLoading(true);

    try {
      const mapTestDetails = await fetchTestResultAnalytics({
        startDate,
        endDate,
        mapTitle,
        secondaryField: selectedSecondaryField,
        signal: controller.signal,
      });

      if (mapTestDetails) {
        setAnalyticsData((prev) => ({
          ...prev,
          map_test_details: mapTestDetails,
        }));
      }
    } finally {
      if (resultTestAbortControllerRef.current === controller) {
        setIsResultTestLoading(false);
      }
    }
  }, [fetchTestResultAnalytics, resultTestDateRange, selectedSecondaryField]);

  const handleSecondaryFieldChange = useCallback(async (secondaryField) => {
    setSelectedSecondaryField(secondaryField);

    const { startDate, endDate } = parseDisplayDateRange(resultTestDateRange);

    if (resultTestAbortControllerRef.current) {
      resultTestAbortControllerRef.current.abort();
    }
    const controller = new AbortController();
    resultTestAbortControllerRef.current = controller;

    setIsResultTestLoading(true);

    try {
      const mapTestDetails = await fetchTestResultAnalytics({
        startDate,
        endDate,
        mapTitle: selectedMap,
        secondaryField,
        signal: controller.signal,
      });

      if (mapTestDetails) {
        setAnalyticsData((prev) => ({
          ...prev,
          map_test_details: mapTestDetails,
        }));
      }
    } finally {
      if (resultTestAbortControllerRef.current === controller) {
        setIsResultTestLoading(false);
      }
    }
  }, [fetchTestResultAnalytics, resultTestDateRange, selectedMap]);

  const handleSummaryDateSearch = (startDate, endDate) => {
    if (!startDate && !endDate) {
      resetSummaryToDefaultDateRange();
      return;
    }

    const [rangeStart, rangeEnd] = summaryDateRange.split(" - ");
    if (rangeStart && rangeEnd) {
      handleSummaryDateChange(rangeStart, rangeEnd);
    }
  };

  const handleResultTestDateSearch = (startDate, endDate) => {
    if (!startDate && !endDate) {
      resetResultTestToDefaultDateRange();
      return;
    }

    const [rangeStart, rangeEnd] = resultTestDateRange.split(" - ");
    if (rangeStart && rangeEnd) {
      handleResultTestDateChange(rangeStart, rangeEnd);
    }
  };

  const graphDataMemo = useMemo(() => analyticsData.test_details, [analyticsData.test_details]);

  const handleOpenSwabList = (category) => {
    setSwabListModal({
      open: true,
      category,
    });
  };

  const handleCloseSwabList = () => {
    setSwabListModal({
      open: false,
      category: null,
    });
  };

  return (
    <StyledScrollbar className="EnvAnalyticsTab__MainContainer--ScrollBar">
      <div className="EnvAnalyticsTab__MainContainer">
        {(isLoading && isInitial) ? (
          <div className="EnvAnalyticsTab__SkeletonContainer">
            <Skeleton.Input width={200} marginBottom={20} active />
            <div className="EnvAnalyticsTab__SkeletonBody--Stats">
              <Skeleton.Input active block className="EnvAnalyticsTab__SkeletonBody--Item" />
            </div>
            <div className="EnvAnalyticsTab__SkeletonBody--Graphs">
              <Skeleton.Input active block className="EnvAnalyticsTab__SkeletonBody--Item" />
            </div>
          </div>
        ) : (
          <>
            <div className="EnvAnalyticsTab__Container">
              <div className="EnvAnalyticsTab__DateFilter">
                <span className="EnvAnalyticsTab__DateFilterTitle">Sample Summary</span>
                { (!isLoading && !isInitial) ? (
                  <DatePicker
                    handleSearchDate={handleSummaryDateSearch}
                    value={summaryDateRange}
                    onChange={handleSummaryDateSelection}
                    getReportDates={getSummaryReportDates}
                    searchOnClear
                    prevValue={summaryPrevDateRange}
                    setPrevValue={setSummaryPrevDateRange}
                    popoverPlacement="bottomRight"
                    popoverClassName="EnvAnalyticsTabPopover"
                    disableClickAway
                    disableSingleDaySelection
                    allowClear
                    datePickerDisplayProps={{
                      displayClassName: "EnvAnalyticsTab__DateDisplayProperties",
                      customCalendarIcon: <CalendarIcon width={18} height={18} />,
                      showText: true,
                    }}
                  />
                ) : (
                  <div className="EnvAnalyticsTab__DateDisplayProperties EnvAnalyticsTab__DateDisplayProperties--Loading">
                    <Spin indicator={<LoadingOutlined className="EnvAnalyticsTab__DateFilter__Spin" spin />} />
                    {summaryDateRange && <span>{summaryDateRange}</span>}
                  </div>
                )}
              </div>
              <div className="EnvAnalyticsTab__Headers">
                {SAMPLE_SUMMARY_CARDS.map(({
                  label, valueKey, swabListCategory, textColor, backgroundColor,
                }) => (
                  <EnvAnalyticsHeader
                    key={valueKey}
                    label={label}
                    value={analyticsData.sample_summary[valueKey]}
                    textColor={textColor}
                    backgroundColor={backgroundColor}
                    onClick={() => handleOpenSwabList(swabListCategory)}
                  />
                ))}
              </div>
              <div className="EnvAnalyticsTab__SummaryChartsRow">
                <div className="EnvAnalyticsTab__ResultSummary">
                  <ResultSummary specDetails={analyticsData.spec_details} dateRange={summaryDateRange} />
                </div>
                <div className="EnvAnalyticsTab__VolumeTrend">
                  <TestVolumeTrendChart graphData={graphDataMemo} />
                </div>
              </div>
              <div className="EnvAnalyticsTab__ResultTestAnalytics">
                <ResultTestAnalyticsChart
                  mapTestDetails={analyticsData.map_test_details}
                  isLoading={isResultTestLoading}
                  dateRange={resultTestDateRange}
                  selectedMap={selectedMap}
                  selectedSecondaryField={selectedSecondaryField}
                  onMapChange={handleMapChange}
                  onSecondaryFieldChange={handleSecondaryFieldChange}
                  onDateChange={handleResultTestDateSelection}
                  onDateSearch={handleResultTestDateSearch}
                  onReset={handleResultTestReset}
                  prevDateRange={resultTestPrevDateRange}
                  setPrevDateRange={setResultTestPrevDateRange}
                  getReportDates={getResultTestReportDates}
                />
              </div>
            </div>
            <div className="EnvAnalyticsTab__TableContainer">
              <div className="EnvAnalyticsTab__Tables">
                <div className="EnvAnalyticsTab__Table EnvAnalyticsTab__Table_Submission">
                  <AnalyticsTable
                    headers={headers}
                    data={analyticsData.submission_details}
                    tableType="SubmissionTracker"
                  />
                </div>
                <div className="EnvAnalyticsTab__Table EnvAnalyticsTab__Table_Ratio">
                  <AnalyticsTable
                    headers={headers_2}
                    data={analyticsData.ratio_details}
                    tableType="AlertTable"
                  />
                </div>
              </div>
            </div>
          </>
        )}
      </div>
      {swabListModal.open && (
        <SwabList
          open={swabListModal.open}
          onClose={handleCloseSwabList}
          category={swabListModal.category}
          dateRange={summaryDateRange}
        />
      )}
    </StyledScrollbar>
  );
}
